import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'

const LIMITE_CARACTERES = 200

const InputEditor = forwardRef(({ value = '', onChange = () => {}, onLimitReached = () => {} }, ref) => {
    const editorRef = useRef(null)
    const selectionRangeRef = useRef(null)

    const salvarCaretAtual = () => {
        const selection = window.getSelection()
        if (!selection.rangeCount) return
        selectionRangeRef.current = selection.getRangeAt(0).cloneRange()
    }

    const posicionarCursorDepoisDoNode = (node) => {
        if (!node) return

        const range = document.createRange()
        const selection = window.getSelection()

        range.setStartAfter(node)
        range.collapse(true)

        selection.removeAllRanges()
        selection.addRange(range)

        selectionRangeRef.current = range.cloneRange()
    }

    const obterTextoPlano = () => {
        const editor = editorRef.current
        if (!editor) return ''

        let formula = ''

        const percorrer = (nodes) => {
            nodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    formula += node.textContent.replace(/\u200B/g, '')
                }

                if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.classList.contains('formula-badge')) {
                        const tipo = node.dataset.type
                        const valor = node.dataset.value

                        if (tipo === 'variavel') formula += `[${valor}]`
                        if (tipo === 'parametro-customizado') formula += `{${valor}}`

                        return
                    }

                    percorrer([...node.childNodes])
                }
            })
        }

        percorrer([...editor.childNodes])

        return formula
    }

    const criarBadge = (texto, tipo, classe) => {
        return `
            <span
                class="${classe} formula-badge"
                contenteditable="false"
                data-type="${tipo}"
                data-value="${texto}"
            >${texto}</span><span class="formula-text-marker">&#8203;</span>
        `
    }

    const processarBadges = () => {
        const editor = editorRef.current
        if (!editor) return

        const conteudoOriginal = editor.innerHTML

        const regexVariavel = /\[([^[\]{}<>]+)\]/g
        const regexParametroCustomizado = /\{([^{}[\]<>]+)\}/g

        let conteudoNovo = conteudoOriginal

        conteudoNovo = conteudoNovo
            .replace(/&nbsp;/g, '')
            .replace(/<div><br><\/div>/g, '')
            .replace(/<div>/g, '')
            .replace(/<\/div>/g, '')
            .replace(/<br>/g, '')

        conteudoNovo = conteudoNovo.replace(
            regexVariavel,
            (_, texto) => criarBadge(texto, 'variavel', 'formula-badge-variavel')
        )

        conteudoNovo = conteudoNovo.replace(
            regexParametroCustomizado,
            (_, texto) => criarBadge(texto, 'parametro-customizado', 'bg-primary')
        )

        conteudoNovo = conteudoNovo.replace(
            /\s*(<span[^>]*formula-badge[^>]*>.*?<\/span><span class="formula-text-marker">.*?<\/span>)\s*/g,
            '$1'
        )

        if (conteudoOriginal !== conteudoNovo) {
            editor.innerHTML = conteudoNovo

            const markers = editor.querySelectorAll('.formula-text-marker')
            const ultimoMarker = markers[markers.length - 1]

            if (ultimoMarker) {
                posicionarCursorDepoisDoNode(ultimoMarker)
            }
        }
    }

    const limitarConteudo = () => {
        const editor = editorRef.current
        if (!editor) return

        let formula = obterTextoPlano()

        if (formula.length <= LIMITE_CARACTERES) return formula

        onLimitReached(true)

        formula = formula.slice(0, LIMITE_CARACTERES)
        editor.innerText = formula

        processarBadges()

        return formula
    }

    const emitirMudanca = () => {
        const formula = obterTextoPlano()
        onChange(formula)
    }

    // Deleta tudo que está dentro da seleção ativa, incluindo badges
    const deletarConteudoSelecionado = () => {
        const selection = window.getSelection()

        if (!selection.rangeCount) {
            return false
        }

        const range = selection.getRangeAt(0)

        if (range.collapsed) {
            return false
        }

        const editor = editorRef.current

        if (!editor) {
            return false
        }

        const fragment = range.cloneContents()
        console.log('badges no fragment:',fragment.querySelectorAll('.formula-badge').length)

        const badgesSelecionadas = []

        fragment.querySelectorAll('.formula-badge').forEach((badge) => {
            badgesSelecionadas.push({
                tipo: badge.dataset.type,
                valor: badge.dataset.value
            })
        })

        editor.querySelectorAll('.formula-badge').forEach((badge) => {
            const encontrou = badgesSelecionadas.find(
                (b) =>
                    b.tipo === badge.dataset.type &&
                    b.valor === badge.dataset.value
            )

            if (encontrou) {
                const marker = badge.nextSibling

                if (
                    marker &&
                    marker.classList &&
                    marker.classList.contains('formula-text-marker')
                ) {
                    marker.remove()
                }

                badge.remove()
            }
        })

        try {
            range.deleteContents()
        } catch (_) { }

        salvarCaretAtual()

        return true
    }

    const substituirBadgeSelecionado = (texto) => {
        const editor = editorRef.current
        if (!editor) return false

        const badgeSelecionado = editor.querySelector('.formula-badge.selected')
        if (!badgeSelecionado) return false

        const marker = badgeSelecionado.nextSibling
        const range = document.createRange()

        if (marker && marker.parentNode) {
            range.setStartBefore(marker)
        } else {
            range.selectNodeContents(editor)
            range.collapse(false)
        }

        range.collapse(true)

        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)

        selectionRangeRef.current = range.cloneRange()

        badgeSelecionado.remove()
        document.body.classList.remove('formula-badge-selected')

        inserirNoCursor(texto, false)

        return true
    }

    const inserirNoCursor = (texto, substituirSelecionado = true) => {
        const editor = editorRef.current
        if (!editor) return

        if (substituirSelecionado && substituirBadgeSelecionado(texto)) return

        const formulaAtual = obterTextoPlano()

        if (formulaAtual.length >= LIMITE_CARACTERES) {
            onLimitReached(true)
            return
        }

        editor.focus()

        const range = selectionRangeRef.current

        if (range) {
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
        }

        document.execCommand('insertText', false, texto.replace(/\s+/g, ''))

        processarBadges()
        limitarConteudo()
        emitirMudanca()
        salvarCaretAtual()
        onLimitReached(obterTextoPlano().length >= LIMITE_CARACTERES)
    }

    useImperativeHandle(ref, () => ({
        inserirNoCursor: (texto, substituirSelecionado = true) => {
            inserirNoCursor(texto, substituirSelecionado)
        },
    }))

    useEffect(() => {
        const editor = editorRef.current
        if (!editor) return

        const textoAtual = obterTextoPlano()
        if (textoAtual === value) return

        editor.innerText = value
        processarBadges()
    }, [value])

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            return
        }

        const selection = window.getSelection()
        if (!selection.rangeCount) return

        const range = selection.getRangeAt(0)

        if (!range.collapsed && (e.key === 'Backspace' || e.key === 'Delete')) {
            e.preventDefault()
            deletarConteudoSelecionado()
            emitirMudanca()
            onLimitReached(obterTextoPlano().length >= LIMITE_CARACTERES)
            return
        }

        const formulaAtual = obterTextoPlano()

        const teclaPermitida = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Tab',
            'Home',
            'End',
        ].includes(e.key)

        if (formulaAtual.length >= LIMITE_CARACTERES && !teclaPermitida) {
            e.preventDefault()
            onLimitReached(true)
            return
        }

        // Backspace com cursor colapsado. Verifica se o nó anterior é uma badge
        if (e.key === 'Backspace' && range.collapsed) {
            let node = range.startContainer
            let offset = range.startOffset
            let targetNode = null

            if (node.nodeType === Node.TEXT_NODE) {
                if (offset > 0) return

                targetNode = node.previousSibling

                if (!targetNode && node.parentNode !== editorRef.current) {
                    targetNode = node.parentNode.previousSibling
                }
            } else {
                targetNode = node.childNodes[offset - 1]
            }

            if (targetNode && targetNode.classList && targetNode.classList.contains('formula-text-marker')) {
                targetNode = targetNode.previousSibling
            }

            if (targetNode && targetNode.classList && targetNode.classList.contains('formula-badge')) {
                e.preventDefault()

                const marker = targetNode.nextSibling
                const novoRange = document.createRange()

                if (marker && marker.parentNode) {
                    novoRange.setStartBefore(marker)
                } else {
                    novoRange.selectNodeContents(editorRef.current)
                    novoRange.collapse(false)
                }

                targetNode.remove()

                if (marker && marker.classList && marker.classList.contains('formula-text-marker')) {
                    marker.remove()
                }

                novoRange.collapse(true)

                const novaSelection = window.getSelection()
                novaSelection.removeAllRanges()
                novaSelection.addRange(novoRange)

                selectionRangeRef.current = novoRange.cloneRange()

                emitirMudanca()
                onLimitReached(obterTextoPlano().length >= LIMITE_CARACTERES)
            }
        }
    }

    const handleClick = (e) => {
        const target = e.target
        const editor = editorRef.current
        if (!editor) return

        const badges = editor.querySelectorAll('.formula-badge')
        badges.forEach((badge) => badge.classList.remove('selected'))

        if (target.classList.contains('formula-badge')) {
            target.classList.add('selected')
            document.body.classList.add('formula-badge-selected')
            return
        }

        document.body.classList.remove('formula-badge-selected')
    }

    const handleCopy = (e) => {
        const selection = window.getSelection()
        if (!selection.rangeCount || selection.isCollapsed) return

        e.preventDefault()

        const range = selection.getRangeAt(0)
        const fragment = range.cloneContents()

        let textoCopiado = ''

        const percorrer = (nodes) => {
            nodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    textoCopiado += node.textContent.replace(/\u200B/g, '')
                }

                if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.classList.contains('formula-badge')) {
                        const tipo = node.dataset.type
                        const valor = node.dataset.value

                        if (tipo === 'variavel') textoCopiado += `[${valor}]`
                        if (tipo === 'parametro-customizado') textoCopiado += `{${valor}}`

                        return
                    }

                    percorrer([...node.childNodes])
                }
            })
        }

        percorrer([...fragment.childNodes])

        e.clipboardData.setData('text/plain', textoCopiado)
    }

    const handlePaste = (e) => {
        e.preventDefault()

        deletarConteudoSelecionado()

        const textoAtual = obterTextoPlano()
        const textoColado = (e.clipboardData || window.clipboardData)
            .getData('text')
            .replace(/\s+/g, '')

        const espacoDisponivel = LIMITE_CARACTERES - textoAtual.length

        if (espacoDisponivel <= 0) {
            onLimitReached(true)
            return
        }

        const excedeu = textoColado.length > espacoDisponivel
        const textoFinal = textoColado.slice(0, espacoDisponivel)

        inserirNoCursor(textoFinal)
        onLimitReached(excedeu)
    }

    const handleInput = () => {
        processarBadges()
        limitarConteudo()
        emitirMudanca()
        salvarCaretAtual()
        onLimitReached(obterTextoPlano().length >= LIMITE_CARACTERES)
    }

    return (
        <div
            ref={editorRef}
            className="form-control formula-editor"
            contentEditable
            suppressContentEditableWarning={true}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onCopy={handleCopy}
            onClick={(e) => {
                salvarCaretAtual()
                handleClick(e)
            }}
            onKeyUp={salvarCaretAtual}
            onMouseUp={salvarCaretAtual}
            onPaste={handlePaste}
        />
    )
})

export default InputEditor